import { useState, useCallback } from 'react';
import { updateLookup } from '../api/lookups';
import type { UpdateLookupRequest, LookupDto } from '../types/lookup';

interface UseUpdateLookupResult {
  update: (id: number, data: UpdateLookupRequest) => Promise<LookupDto>;
  isLoading: boolean;
  error: string | null;
  reset: () => void;
}

/**
 * Hook for updating a lookup item
 */
export function useUpdateLookup(
  type: string,
  onSuccess?: (lookup: LookupDto) => void
): UseUpdateLookupResult {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const update = useCallback(
    async (id: number, data: UpdateLookupRequest): Promise<LookupDto> => {
      setIsLoading(true);
      setError(null);
      try {
        const result = await updateLookup(type, id, data);
        onSuccess?.(result);
        return result;
      } catch (err) {
        const apiError = err as { response?: { data?: { message?: string } } };
        const message = apiError.response?.data?.message || 'Failed to update item';
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [type, onSuccess]
  );

  const reset = useCallback(() => {
    setError(null);
  }, []);

  return {
    update,
    isLoading,
    error,
    reset,
  };
}
